/* ==========================================================================
   whero-notify-me.js — Klaviyo client-side subscribe + success state
   --------------------------------------------------------------------------
   Submits [data-whero-notify-form] to Klaviyo's client-side API without
   reloading the page, fires a 'Subscribed to Restock Alert' custom event,
   persists opt-in state in localStorage.whero_restock_subs (array of
   flavor slugs only — no PII), and restores the success panel on repeat
   visits so we don't re-prompt the same user on the same device.

   Klaviyo credentials come from window.WHERO_KLAVIYO which the consuming
   section should emit from Liquid:
     <script>
       window.WHERO_KLAVIYO = {
         publicKey: {{ settings.klaviyo_public_key | json }},
         restockListId: {{ settings.klaviyo_restock_list_id | json }}
       };
     </script>

   If the public key is missing, the form degrades to a console.warn
   and no network call is attempted — the submit simply does nothing
   rather than surfacing a red error to a visitor who can't fix it.

   Source of truth for "who wants flavor X" is the 'Subscribed to Restock
   Alert' custom event we fire on every successful submit, carrying
   {flavor_slug, flavor_name, source}. Klaviyo segments (manual for Phase
   1, automated flow in Phase 2 via the Toast-bridge) query the event
   directly — no profile-level array to keep in sync.

   NO form values or PII are written to localStorage. Only the slug +
   "subscribed:true" is persisted so the UI can reflect prior opt-ins.
   ========================================================================== */
(function () {
  'use strict';

  var KLAVIYO_REVISION = '2024-06-15';
  var STORAGE_KEY = 'whero_restock_subs';

  function klaviyoConfig() {
    var cfg = window.WHERO_KLAVIYO || {};
    return {
      publicKey: cfg.publicKey || '',
      restockListId: cfg.restockListId || '',
    };
  }

  /* ---- persistence: slug array only, no PII ---------------------------- */

  function readSubs() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      var parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.filter(function (x) { return typeof x === 'string'; }) : [];
    } catch (_) { return []; }
  }

  function writeSubs(arr) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(arr));
    } catch (_) { /* storage disabled — non-fatal */ }
  }

  function rememberSub(slug) {
    if (!slug) return;
    var subs = readSubs();
    if (subs.indexOf(slug) === -1) subs.push(slug);
    writeSubs(subs);
    return subs;
  }

  function hasSubscribed(slug) {
    return readSubs().indexOf(slug) !== -1;
  }

  /* ---- Klaviyo API calls ----------------------------------------------- */

  function klaviyoSubscribe(publicKey, listId, email, phone, smsConsent, profileProperties) {
    var url = 'https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(publicKey);

    var profileAttrs = {
      properties: profileProperties || {},
    };
    if (email) profileAttrs.email = email;
    if (phone) profileAttrs.phone_number = phone;

    // Klaviyo's current API expects `subscriptions` as a SIBLING of
    // `attributes` under `profile.data` — not nested inside attributes.
    // The older nested shape returns:
    //   400 "subscriptions is not a valid field for the resource profile"
    var subscriptions = {};
    if (email) {
      subscriptions.email = { marketing: { consent: 'SUBSCRIBED' } };
    }
    if (phone && smsConsent) {
      subscriptions.sms = { marketing: { consent: 'SUBSCRIBED' } };
    }

    var profileData = {
      type: 'profile',
      attributes: profileAttrs,
    };
    if (Object.keys(subscriptions).length) {
      profileData.subscriptions = subscriptions;
    }

    var body = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'wynstons.com restock alert',
          profile: { data: profileData },
        },
        relationships: {
          list: {
            data: { type: 'list', id: listId },
          },
        },
      },
    };

    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'revision': KLAVIYO_REVISION,
      },
      body: JSON.stringify(body),
    });
  }

  function klaviyoTrackEvent(publicKey, email, phone, metricName, properties) {
    var url = 'https://a.klaviyo.com/client/events/?company_id=' + encodeURIComponent(publicKey);

    var profileAttrs = {};
    if (email) profileAttrs.email = email;
    if (phone) profileAttrs.phone_number = phone;

    var body = {
      data: {
        type: 'event',
        attributes: {
          properties: properties || {},
          metric: {
            data: {
              type: 'metric',
              attributes: { name: metricName },
            },
          },
          profile: {
            data: {
              type: 'profile',
              attributes: profileAttrs,
            },
          },
        },
      },
    };

    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'revision': KLAVIYO_REVISION,
      },
      body: JSON.stringify(body),
    });
  }

  /* ---- UI helpers ----------------------------------------------------- */

  function emailLooksValid(s) {
    // Light client-side check; the server is the real validator.
    return typeof s === 'string' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
  }

  function phoneLooksValid(s) {
    // Accept digits, spaces, hyphens, parens, plus. At least 10 digits.
    if (!s) return true; // optional field
    var digits = s.replace(/[^\d]/g, '');
    return digits.length >= 10;
  }

  function setStatus(statusEl, message) {
    if (!statusEl) return;
    statusEl.textContent = '';
    if (!message) {
      statusEl.setAttribute('hidden', '');
      return;
    }
    var p = document.createElement('p');
    p.style.margin = '0';
    p.textContent = message;
    statusEl.appendChild(p);

    var retry = document.createElement('button');
    retry.type = 'button';
    retry.className = 'whero-notify__retry';
    retry.textContent = 'Try again';
    retry.addEventListener('click', function () {
      setStatus(statusEl, '');
    });
    statusEl.appendChild(retry);

    statusEl.removeAttribute('hidden');
  }

  function showSuccess(rootEl) {
    var form = rootEl.querySelector('[data-whero-notify-form]');
    var success = rootEl.querySelector('[data-whero-notify-success]');
    if (form) form.setAttribute('hidden', '');
    if (success) success.removeAttribute('hidden');
  }

  /* ---- wire a single instance ----------------------------------------- */

  function wire(rootEl) {
    if (rootEl.__wheroNotifyWired) return;
    rootEl.__wheroNotifyWired = true;

    var slug = rootEl.getAttribute('data-flavor-slug') || '';
    var flavorName = rootEl.getAttribute('data-flavor-name') || '';
    var source = rootEl.getAttribute('data-source') || 'card';

    // Restore prior opt-in.
    if (slug && hasSubscribed(slug)) {
      showSuccess(rootEl);
      return;
    }

    var form = rootEl.querySelector('[data-whero-notify-form]');
    if (!form) return;

    var statusEl = rootEl.querySelector('[data-whero-notify-status]');
    var submitBtn = form.querySelector('.whero-notify__submit');
    var emailInput = form.querySelector('input[name="email"]');
    var phoneInput = form.querySelector('input[name="phone"]');
    var consentInput = form.querySelector('input[name="sms_consent"]');

    // Merchant-configurable copy, with defaults matching the Liquid defaults.
    var copy = {
      errEmail:   rootEl.getAttribute('data-copy-error-email')   || "That email looks off — mind double-checking?",
      errPhone:   rootEl.getAttribute('data-copy-error-phone')   || 'Phone number needs at least 10 digits.',
      errNetwork: rootEl.getAttribute('data-copy-error-network') || "Couldn't save that — try again in a sec?",
    };

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      setStatus(statusEl, '');

      var email = emailInput ? emailInput.value.trim() : '';
      var phone = phoneInput ? phoneInput.value.trim() : '';
      var smsConsent = !!(consentInput && consentInput.checked);

      if (!emailLooksValid(email)) {
        if (emailInput) emailInput.setAttribute('aria-invalid', 'true');
        setStatus(statusEl, copy.errEmail);
        if (emailInput) emailInput.focus();
        return;
      }
      if (emailInput) emailInput.removeAttribute('aria-invalid');

      if (phone && !phoneLooksValid(phone)) {
        if (phoneInput) phoneInput.setAttribute('aria-invalid', 'true');
        setStatus(statusEl, copy.errPhone);
        if (phoneInput) phoneInput.focus();
        return;
      }
      if (phoneInput) phoneInput.removeAttribute('aria-invalid');

      // If phone provided but consent NOT checked, drop the phone silently
      // rather than erroring — user likely filled it then un-checked.
      var sendPhone = (phone && smsConsent) ? phone : '';

      var cfg = klaviyoConfig();
      if (!cfg.publicKey || !cfg.restockListId) {
        console.warn('[whero-notify-me] Klaviyo credentials missing; form submit skipped');
        return;
      }

      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.setAttribute('aria-busy', 'true');
      }

      // No per-flavor profile properties on the subscribe call — the
      // 'Subscribed to Restock Alert' custom event (fired below on
      // success) is the durable record of which flavors this profile
      // wants. Keeps the client stateless and avoids the cross-device
      // "append to list-typed property" problem.
      klaviyoSubscribe(cfg.publicKey, cfg.restockListId, email, sendPhone, smsConsent, {})
        .then(function (res) {
          if (!res.ok && res.status !== 202) {
            throw new Error('HTTP ' + res.status);
          }
          // Success path. Fire the custom event — non-blocking.
          klaviyoTrackEvent(cfg.publicKey, email, sendPhone, 'Subscribed to Restock Alert', {
            flavor_slug: slug,
            flavor_name: flavorName,
            source: source,
            // Preferred shop for per-location restock targeting. Read at
            // submit time (not wire time) so the current-flavors shop
            // selector can update it after the form is wired. Empty →
            // the bridge treats it as "any" (notify for either location).
            shop: rootEl.getAttribute('data-shop') || '',
          }).catch(function () { /* tracking failures are silent */ });

          rememberSub(slug);
          showSuccess(rootEl);
        })
        .catch(function () {
          setStatus(statusEl, copy.errNetwork);
        })
        .then(function () {
          if (submitBtn) {
            submitBtn.disabled = false;
            submitBtn.removeAttribute('aria-busy');
          }
        });
    });
  }

  function init() {
    var roots = document.querySelectorAll('[data-whero-notify]');
    for (var i = 0; i < roots.length; i++) wire(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-scan after section re-render in the theme editor.
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', init);

  // Expose init so sections that inject [data-whero-notify] nodes after
  // initial DOMContentLoaded (e.g., the flavor-vote archive grid, which
  // builds cards client-side) can wire them. wire() is idempotent.
  window.wheroNotifyMeInit = init;

  // Expose the Klaviyo primitives so other surfaces (e.g. the
  // /pages/notify-me flavor-search page) can reuse the exact same
  // subscribe + event-tracking call shape instead of duplicating the API
  // contract (revision pin, body structure, list-vs-event split). Thin
  // wrappers pull credentials from window.WHERO_KLAVIYO via klaviyoConfig()
  // so callers only ever pass user data. Additive — does not change any
  // behavior of the inline card/PDP forms above.
  window.wheroNotifyMe = {
    config: klaviyoConfig,
    isConfigured: function () {
      var c = klaviyoConfig();
      return !!(c.publicKey && c.restockListId);
    },
    // Subscribe an email/phone to the restock list. Resolves to the fetch
    // Response (caller checks res.ok || res.status === 202).
    subscribe: function (email, phone, smsConsent, profileProperties) {
      var c = klaviyoConfig();
      return klaviyoSubscribe(c.publicKey, c.restockListId, email, phone, !!smsConsent, profileProperties || {});
    },
    // Fire a custom event (the durable per-flavor record). Non-blocking
    // for the caller; tracking failures should be swallowed.
    trackEvent: function (email, phone, metricName, properties) {
      var c = klaviyoConfig();
      return klaviyoTrackEvent(c.publicKey, email, phone, metricName, properties || {});
    },
    // localStorage helpers (slug-only, no PII) so other surfaces share the
    // same "already on the list" memory as the inline forms.
    remember: rememberSub,
    hasSubscribed: hasSubscribed,
  };
})();
